// CP-857: helpers for the TenStreet code table screens.
//
// Everything here is config-driven -- it takes a CODE_TABLES entry rather than knowing
// about either table -- so the two screens (and any future one) share it.

import { canonicalize, parseCsvLine, splitCsvLines } from '../../utils/csv';

/**
 * Booleans arrive from SQL as `true`/`false` via the mssql driver, from a CSV as text, and
 * from the form as a real boolean. Normalize all three the same way the API does, so the
 * grid, the export and a re-import agree.
 */
export const toBool = (value, defaultValue = true) => {
  if (value === undefined || value === null || String(value).trim() === '') {
    return defaultValue;
  }
  if (typeof value === 'boolean') return value;
  const text = String(value).trim().toLowerCase();
  return text === '1' || text === 'true' || text === 'yes' || text === 'y';
};

/** Booleans display and export as Yes / No -- the same words the audit trail records. */
export const boolLabel = value => (toBool(value, false) ? 'Yes' : 'No');

export const emptyRow = config =>
  config.fields.reduce(
    // A new row starts with the flags on: a status is added in order to count, a mapping in
    // order to be active. Matches the procs' defaults.
    (row, field) => ({ ...row, [field.key]: field.type === 'boolean' ? true : '' }),
    {},
  );

/**
 * Trim text, coerce flags, leave blanks blank.
 *
 * Blank numerics stay '' and the API maps them to NULL, which is a meaningful value here:
 * a worklist with no branch is either a DMS aggregate or deliberately unmapped.
 */
export const normalizeRow = (config, row = {}) =>
  config.fields.reduce((out, field) => {
    if (field.type === 'boolean') {
      return { ...out, [field.key]: toBool(row[field.key]) };
    }
    return { ...out, [field.key]: String(row[field.key] ?? '').trim() };
  }, {});

/**
 * Client-side validation for the add/edit form.
 *
 * Mirrors the stored procedure's rules for immediate feedback, but is not the enforcement
 * point -- the proc re-checks everything, including the duplicate-key rule that needs the
 * database. Returns a { fieldKey: message } map; empty means valid.
 */
export const validateRow = (config, row = {}) => {
  const errors = {};

  config.fields.forEach(field => {
    const value = String(row[field.key] ?? '').trim();

    if (field.required && field.type !== 'boolean' && !value) {
      errors[field.key] = `${field.label} is required.`;
      return;
    }
    if (field.type === 'number' && value) {
      if (!/^\d+$/.test(value)) {
        errors[field.key] = `${field.label} must be a whole number.`;
      } else if (Number(value) < 0) {
        errors[field.key] = `${field.label} cannot be negative.`;
      }
    }
  });

  return errors;
};

/**
 * Values the pick-list endpoint says TenStreet has actually sent.
 *
 * Used for a warning, never to constrain the input: the procs accept an unseen value on
 * purpose, because a status can be classified — or a worklist mapped — ahead of its first
 * application. Same shape of nudge as SiteList's "new value" warning on the people fields.
 */
export const knownValues = (config, pickListRows = []) =>
  new Set(
    pickListRows
      .map(item => String(item?.[config.pickList.valueKey] ?? '').trim())
      .filter(Boolean)
      .map(value => value.toUpperCase()),
  );

export const isUnseenValue = (value, known) => {
  const text = String(value ?? '').trim();
  return !!text && known.size > 0 && !known.has(text.toUpperCase());
};

/**
 * One line describing what the pick-list says about this table's coverage.
 *
 * Delegates to the table's own `summarize`, because the useful statement differs per table
 * and a generic "N values are unlisted" count is actively WRONG for both: each table is an
 * opt-in / exceptions list, so a value having no row is the normal state, not a backlog.
 * See the comments on each `summarize` in config.js.
 */
export const pickListSummary = (config, pickListRows = []) =>
  typeof config?.summarize === 'function' ? config.summarize(pickListRows) : '';

/**
 * Rows shaped for ExportCsvButton -- labels as headers, so an exported file can be edited
 * and fed straight back through the import.
 */
export const toCsvData = (config, rows = []) =>
  rows.map(row =>
    config.fields.reduce(
      (out, field) => ({
        ...out,
        [field.label]: field.type === 'boolean' ? boolLabel(row[field.key]) : (row[field.key] ?? ''),
      }),
      {},
    ),
  );

/**
 * Parse a pasted or uploaded CSV into rows for this table.
 *
 * The header row maps by label OR key, case- and space-insensitively, so both an export
 * from this screen and a hand-maintained spreadsheet work.
 *
 * Returns { rows, errors } -- errors are structural (unknown, missing or absent columns),
 * not value validation, which the server does.
 */
export const parseCsv = (config, text) => {
  const lines = splitCsvLines(String(text ?? '').trim());
  if (lines.length < 2) {
    return { rows: [], errors: ['The file needs a header row and at least one data row.'] };
  }

  // Keep the headers as written as well as canonicalized: matching ignores case and
  // spacing, but a message about an unrecognized column must echo what the user typed.
  const rawHeaderCells = parseCsvLine(lines[0]).map(cell => String(cell ?? '').trim());
  const headerCells = rawHeaderCells.map(cell => canonicalize(cell));
  const fieldByHeader = new Map();
  config.fields.forEach(field => {
    fieldByHeader.set(canonicalize(field.label), field.key);
    fieldByHeader.set(canonicalize(field.key), field.key);
  });

  const columnKeys = headerCells.map(header => fieldByHeader.get(header) ?? null);

  const missing = config.fields
    .filter(field => field.required && !columnKeys.includes(field.key))
    .map(field => field.label);
  if (missing.length) {
    return { rows: [], errors: [`Missing required column(s): ${missing.join(', ')}.`] };
  }

  const unknown = rawHeaderCells.filter((header, index) => columnKeys[index] === null && header);
  const errors = unknown.length ? [`Ignoring unrecognized column(s): ${unknown.join(', ')}.`] : [];

  // A column absent from the file imports as blank, and a Merge writes that blank over
  // whatever the row holds today -- so an older spreadsheet silently clears it. Warn rather
  // than block: importing a subset to deliberately clear it is legitimate, on purpose.
  const absent = config.fields
    .filter(field => !field.required && !columnKeys.includes(field.key))
    .map(field => field.label);
  if (absent.length) {
    errors.push(
      `Not in the file: ${absent.join(', ')}. Those columns will be saved as blank — ` +
        'add them to the file to keep the current values.',
    );
  }

  const rows = lines.slice(1).map(line => {
    const cells = parseCsvLine(line);
    const row = emptyRow(config);
    columnKeys.forEach((key, index) => {
      if (key) {
        row[key] = String(cells[index] ?? '').trim();
      }
    });
    return normalizeRow(config, row);
  });

  // A trailing newline yields one blank row; drop anything with no text in it.
  const isBlank = row =>
    config.fields.filter(field => field.type !== 'boolean').every(field => !String(row[field.key] ?? '').trim());

  return { rows: rows.filter(row => !isBlank(row)), errors };
};

/**
 * Render an audit timestamp for display. ChangedAt is stored UTC and the driver hands it
 * back as UTC, so this is the one place that turns it into the reader's local time.
 */
export const formatWhen = value => {
  if (!value) return '';
  const date = new Date(value);
  return Number.isNaN(date.getTime()) ? String(value) : date.toLocaleString();
};

const toIsoWhen = value => {
  if (!value) return '';
  const date = new Date(value);
  return Number.isNaN(date.getTime()) ? String(value) : date.toISOString();
};

/**
 * Group the audit endpoint's rows back into one entry per change.
 *
 * tb_cl_cp_bi_codetable_audit is long format -- one row per changed FIELD -- so an edit
 * that touched three columns arrives as three rows sharing a ChangeId. The proc already
 * orders newest-first and never splits a change across the row cap, so this preserves the
 * order it was given rather than re-sorting.
 *
 * A change with no field rows (FieldName null) is an edit that moved nothing; it is kept
 * so the history does not silently drop something a user did.
 */
export const groupAuditChanges = (rows = []) => {
  const byChange = new Map();

  rows.forEach(row => {
    const id = row?.ChangeId;
    if (!id) return;

    if (!byChange.has(id)) {
      byChange.set(id, {
        ChangeId: id,
        ChangedAt: row.ChangedAt,
        ChangedBy: row.ChangedBy,
        ChangeAction: row.ChangeAction,
        KeyValue: row.KeyValue,
        BatchId: row.BatchId,
        changes: [],
      });
    }
    if (row.FieldName) {
      byChange.get(id).changes.push({
        label: row.FieldName,
        // An INSERT has no before and a DELETE has no after; null distinguishes "no side"
        // from "changed to blank", which the dialog renders differently.
        from: row.ChangeAction === 'INSERT' || row.ChangeAction === 'SEED' ? null : (row.OldValue ?? ''),
        to: row.ChangeAction === 'DELETE' ? null : (row.NewValue ?? ''),
      });
    }
  });

  return [...byChange.values()];
};

/**
 * Change history shaped for ExportCsvButton.
 *
 * Already long -- one row per changed field -- because that is how the audit table stores
 * it, so the export is close to a pass-through. Two timestamps by design: `When` matches
 * the screen (local, so the two reconcile) and `When (ISO)` is sortable, because a locale
 * string sorts as text and puts 10/1 before 9/1.
 */
export const toAuditCsvData = (rows = []) =>
  rows.map(row => ({
    When: formatWhen(row.ChangedAt),
    'When (ISO)': toIsoWhen(row.ChangedAt),
    Who: row.ChangedBy ?? '',
    Action: row.ChangeAction ?? '',
    Key: row.KeyValue ?? '',
    Field: row.FieldName ?? '',
    From: row.OldValue ?? '',
    To: row.NewValue ?? '',
    'Audit Id': row.AuditId ?? '',
    // Groups the rows of one bulk import, which is how a whole previous list is recovered.
    'Batch Id': row.BatchId ?? '',
  }));
